import styled from "styled-components";
import { Color } from "../../../constants/color";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  DatiDipendenteDaSalvare,
  Employee,
  EmployeeNominations,
  EmployeeSpecificRisks,
  EmployeeWorkplaces,
  EmployeeEquipments,
  Nomination,
} from "../../../types";
import { BE_BASE_URL } from "../../../utils/config";
import * as Ariakit from "@ariakit/react";
import SubmitButton from "../../form/submit";
import Label from "../../label";
import { Select, SelectItem, SelectProvider } from "../../form/select";
import { Status } from "../../../constants/status";
import { formatDate } from "../../../features/employee/utils";
import Button from "../../button/button";
import { Icons } from "../../../constants/icons";
import { CircleX, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../../provider/authProvider";
import { ModalContent, ModalLabel } from "./ModalStyles";
import { toast } from "react-toastify";
import { useAppSelector } from "../../../app/hooks";
import { selectUser } from "../../../features/user/userSlice";
import { puoEliminareDipendenti } from "../../../constants/role";
import { useSolaLettura } from "../../../features/user/solaLettura";
import {
  eliminaDipendente,
  salvaDipendente,
} from "../../../features/employee/utils";
import { avvisoUnico } from "../../../features/notifiche/avvisi";
import { useSediDiLavoro } from "../../../features/employee/useSediDiLavoro";
import { chiaveSede } from "../../../features/certificate/pipeline";
import { matchSorter } from "match-sorter";
import { ZIndex } from "../../../constants/z-index";
import { codiceFiscaleDaCorreggere } from "../../../features/employee/rilievoCodiceFiscale";
import DialogConferma from "../../modal/conferma";
import AttestatiDipendente from "../attestati-dipendente";
import Suggerimento from "../../tooltip/suggerimento";

/*
 Identita' dei due avvisi della modale. Servono perche' di ciascuno a schermo ce ne sia sempre
 uno solo, l'ultimo: senza, un secondo tentativo lascerebbe accanto alla conferma l'errore del
 primo, gia' superato.
*/
const AVVISO_SALVATAGGIO = "dipendente-salvataggio";
const AVVISO_RILIEVO = "dipendente-salvataggio-rilievo";
const AVVISO_STATO = "dipendente-salvataggio-stato";

// Definire uno stile per il contenitore dei pulsanti
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 2rem;
  padding-block: 1rem;
  align-items: center;
`;

/*
 L'eliminazione sta all'estremo opposto dei bottoni che salvano: e' un comando che non si
 preme per sbaglio mentre si cerca "Salva".
*/
const EliminaAllineatoASinistra = styled.div`
  margin-right: auto;
`;

const BadgeDiv = styled.div`
  border: 1px solid ${Color.BLUE};
  background-color: ${Color.LIGHT_GREY};
  border-radius: 1rem;
  padding: 4px 8px;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const FormWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 2rem;
  row-gap: 0.5rem;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

/*
 `$rilievo` colora il bordo del campo su cui c'e' qualcosa da correggere. Il colore da solo non
 dice niente a chi non distingue il rosso: va sempre insieme al testo di `RilievoCampo`, che e'
 la parte che spiega, e all'`aria-describedby` che li lega.
*/
const Input = styled.input<{ $rilievo?: boolean }>`
  border: 1px solid
    ${({ $rilievo }) => ($rilievo ? Color.RED : Color.LIGHT_BLUE)};
  border-radius: 1rem;
  padding: 0.5rem 1rem;
  font-size: 1rem;
`;

/*
 Il rilievo su un singolo campo: dice a parole che cosa non va, sotto il campo che lo riguarda.
 Non e' un errore bloccante — il dipendente e' in anagrafica e ci resta (ADR-0003) — ma va letto
 come una cosa da sistemare, non come una nota di colore.
*/
const RilievoCampo = styled.p`
  margin: 0;
  color: ${Color.RED};
  font-size: 0.8rem;
  line-height: 1.2;
`;

/*
 La tendina dei suggerimenti della sede di lavoro. Non e' portata fuori dal documento: come le
 select della scheda sta dentro il portale della dialog, dove il livello `POPOVER` basta a
 tenerla sopra il modulo.
*/
const SuggerimentiPopover = styled(Ariakit.ComboboxPopover)`
  z-index: ${ZIndex.POPOVER};
  display: flex;
  flex-direction: column;
  max-height: min(var(--popover-available-height, 240px), 240px);
  overflow: auto;
  overscroll-behavior: contain;
  border: 1px solid ${Color.GREY};
  border-radius: 8px;
  background-color: white;
  color: ${Color.LIGHT_BLUE};
  box-shadow: 0 10px 15px -3px var(--shadow), 0 4px 6px -4px var(--shadow);
`;

const SuggerimentoSede = styled(Ariakit.ComboboxItem)`
  padding: 0.5rem 1rem;
  cursor: default;
  outline: none;

  &[data-active-item] {
    background-color: ${Color.LIGHT_GREY};
  }
`;

const TextArea = styled.textarea`
  border: 1px solid ${Color.LIGHT_BLUE};
  border-radius: 1rem;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  font-family: inherit;
`;

const selectStyle = {
  "--padBlock": "0.3rem",
  border: "1px solid var(--blue300)",
  borderRadius: "1rem",
  fontSize: "1rem",
} as React.CSSProperties;

const selectNominationsStyle = {
  "--padBlock": "0.3rem",
  border: "1px solid var(--blue300)",
  borderRadius: "1rem",
  fontSize: "1rem",
  //width: "27.3rem",
} as React.CSSProperties;

interface ModalEmployeeEditProps {
  isVisible: boolean;
  employee: Employee | null;
  closeModal: () => void;
  isNewEmployee: boolean;
  handleSubmitSuccess: (employee: Employee) => void;
}

const ModalEmployeeEdit = ({
  isVisible,
  employee,
  closeModal,
  handleSubmitSuccess,
  isNewEmployee,
}: ModalEmployeeEditProps) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState(employee?.firstName || "");
  const [lastName, setLastName] = useState(employee?.lastName || "");
  const [cf, setCf] = useState(employee?.cf || "");
  const [email, setEmail] = useState(employee?.email || "");
  const [workplace, setWorkplace] = useState(employee?.workplace || "");
  const [phone, setPhone] = useState(employee?.phone || "");
  const [jobDescription, setJobDescription] = useState(
    employee?.jobDescription || "",
  );
  const [hireDate, setHireDate] = useState(
    formatDate(employee?.hireDate, "yyyy-MM-dd") || "",
  );
  const [cessationDate, setCessationDate] = useState(
    formatDate(employee?.cessationDate, "yyyy-MM-dd") || "",
  );
  const [birthDate, setBirthDate] = useState(
    formatDate(employee?.birthDate, "yyyy-MM-dd") || "",
  );
  const [birthPlace, setBirthPlace] = useState(employee?.birthPlace || "");
  const [employeeNominations, setEmployeeNominations] = useState<
    EmployeeNominations[]
  >([]);
  const [employeeRisks, setEmployeeRisks] = useState<EmployeeSpecificRisks[]>(
    [],
  );
  const [workplaces, setWorkplaces] = useState<EmployeeWorkplaces[]>([]);
  const [equipments, setEquipments] = useState<EmployeeEquipments[]>([]);
  const [nominationsOptions, setNominationsOptions] = useState<Nomination[]>(
    [],
  );
  const [selectedState, setSelectedState] = useState("");
  const [description, setDescription] = useState(employee?.description || "");
  const [confermaEliminazione, setConfermaEliminazione] = useState(false);
  const utente = useAppSelector(selectUser);
  // In sola lettura (abbonamento disattivato, ODOO 300) la scheda si consulta, attestati
  // compresi, ma non si salva e non si elimina: il backend lo rifiuterebbe.
  const solaLettura = useSolaLettura();
  const puoEliminare = puoEliminareDipendenti(utente?.role) && !solaLettura;

  /*
   I suggerimenti del campo "Sede di lavoro": le sedi gia' in uso in azienda, filtrate su quel
   che si sta scrivendo. Il campo resta libero — la sede di lavoro e' dichiarata sul dipendente
   e non e' vincolata all'elenco delle sedi dell'azienda — e i suggerimenti servono solo a non
   far nascere la gemella di una sede che c'e' gia'. Il confronto passa da `chiaveSede`, la
   stessa normalizzazione con cui le voci sono state costruite.
  */
  const sediSuggerite = useSediDiLavoro(isVisible);
  const suggerimentiSede = useMemo(
    () =>
      matchSorter(sediSuggerite, chiaveSede(workplace), {
        threshold: matchSorter.rankings.CONTAINS,
      }),
    [sediSuggerite, workplace],
  );

  /*
   Il rilievo sul codice fiscale e' lo stesso che nell'elenco accende la barra rossa sulla riga:
   qui pero' si legge per esteso, perche' e' aprendo la scheda che lo si va a correggere. Si
   passa il codice a video perche' qui si puo' modificare: la regola sta in
   `codiceFiscaleDaCorreggere`.
  */
  const cfDaCorreggere = codiceFiscaleDaCorreggere(employee, cf);

  /*
   L'eliminazione e' logica: il dipendente sparisce dagli elenchi ma il suo storico resta, e
   reinserirlo con lo stesso codice fiscale lo riprende. Chiudendo il modale la pagina
   ricarica lista e limite dell'abbonamento da se'.
  */
  const eliminaConfermato = async () => {
    if (!employee?.id) {
      return;
    }
    const eliminato = await eliminaDipendente(employee.id);
    setConfermaEliminazione(false);
    if (eliminato) {
      toast.success(t("employee-delete-riuscita"));
      closeModal();
    }
  };

  useEffect(() => {
    if (employee) {
      setFirstName(employee.firstName || "");
      setLastName(employee.lastName || "");
      setCf(employee.cf || "");
      setEmail(employee.email || "");
      setWorkplace(employee.workplace || "");
      setPhone(employee.phone || "");
      setJobDescription(employee.jobDescription || "");
      setHireDate(formatDate(employee.hireDate, "yyyy-MM-dd") || "");
      setCessationDate(formatDate(employee.cessationDate, "yyyy-MM-dd") || "");
      setBirthDate(formatDate(employee.birthDate, "yyyy-MM-dd") || "");
      setBirthPlace(employee.birthPlace || "");
      setSelectedState(employee.state ? String(employee.state) : "");
      setDescription(employee.description || "");
    }
  }, [employee]);
  //const [selectedNomination, setSelectedNomination] = useState<string>("");
  const pageSize = 1000;
  const pageNumber = 0;

  useEffect(() => {
    const fetchNominations = async () => {
      try {
        const response = await axiosInstance.get(`${BE_BASE_URL}/nominations`);
        // console.log("Nominations response:", response.data);
        setNominationsOptions(response.data);
      } catch (error) {
        //console.error("Error fetching nominations", error);
      }
    };

    fetchNominations();
  }, []);

  useEffect(() => {
    // console.log("ModalEmployeeEdit useEffect", { employee });

    if (!isNewEmployee && employee?.id) {
      axiosInstance
        .get(`${BE_BASE_URL}/employee-nominations/${employee.id}`)
        .then((response) => {
          // console.log("Nominations response:", response.data);
          if (response.data && response.data) {
            setEmployeeNominations(response.data);
          } else {
            setEmployeeNominations([]);
          }
        });
      // .catch((error) => console.error("Error fetching nominations", error));

      axiosInstance
        .get(
          `${BE_BASE_URL}/specificRisks/${employee.id}?size=${pageSize}&page=${pageNumber}`,
        )
        .then((response) => {
          // console.log("Specific Risks response:", response.data);
          if (response.data && response.data.content) {
            setEmployeeRisks(response.data.content);
          } else {
            setEmployeeRisks([]);
          }
        });
      /*
        .catch((error) =>
          //console.error("Error fetching specific risks", error)
        );
      */
      axiosInstance
        .get(
          `${BE_BASE_URL}/workplaces/${employee.id}?size=${pageSize}&page=${pageNumber}`,
        )
        .then((response) => {
          // console.log("Workplaces response:", response.data);
          if (response.data && response.data.content) {
            setWorkplaces(response.data.content);
          } else {
            setWorkplaces([]);
          }
        });
      //.catch((error) => console.error("Error fetching workplaces", error));
      axiosInstance
        .get(
          `${BE_BASE_URL}/equipments/${employee.id}?size=${pageSize}&page=${pageNumber}`,
        )
        .then((response) => {
          // console.log("Equipment response:", response.data);
          if (response.data && response.data.content) {
            setEquipments(response.data.content);
          } else {
            setEquipments([]);
          }
        });
      // .catch((error) => console.error("Error fetching equipment", error));
    } else {
      // When adding a new employee, initialize as empty arrays
      setEmployeeNominations([]);
      setEmployeeRisks([]);
      setWorkplaces([]);
      setEquipments([]);
    }
  }, [employee, isNewEmployee]);

  const handleSelectNomination = (nomination: string) => {
    if (nomination) {
      const selectedOption = nominationsOptions.find(
        (n) => n.name === nomination,
      );
      if (selectedOption) {
        setEmployeeNominations((prev) => [
          ...prev,
          { id: selectedOption.id, nomination: selectedOption },
        ]);
        //setSelectedNomination(""); // Resetta la selezione dopo l'aggiunta
        //nominationStore.setValue(""); // Resetta il select
      }
    }
  };

  const nominationStore = Ariakit.useSelectStore({
    value: "", //selectedNomination,
    setValue: (value) => {
      //setSelectedNomination(value);
      handleSelectNomination(value);
    },
  });

  const stateStore = Ariakit.useSelectStore({
    value: selectedState,
    setValue: (value) => {
      setSelectedState(value);
    },
  });

  /*
   Un salvataggio solo per i due bottoni della modale.

   Erano due copie della stessa chiamata, una per «Salva» e una per «Salva e analizza», e
   divergevano: una passava il dipendente alla pagina, l'altra no. L'unica differenza vera e'
   che cosa succede dopo, e sta nel parametro.

   Che cosa vede l'operatore, adesso:
   - dipendente salvato: avviso verde e modale chiusa (la pagina ricarica l'elenco da se');
   - salvataggio rifiutato: avviso rosso col testo che manda il backend, e modale che resta
     aperta con dentro i dati appena scritti, da correggere senza riscriverli;
   - dipendente salvato con un rilievo (il codice fiscale che non supera la verifica non
     blocca il salvataggio, per scelta): l'avviso verde arriva lo stesso e la modale si
     chiude, perche' il dipendente e' in anagrafica, e il rilievo si mostra accanto in
     giallo. Non e' un errore e non va vestito da errore. Stessa sorte per lo stato dedotto
     dalla data di cessazione.
  */
  const salva = async (analizza: boolean) => {
    // Anche dall'invio del modulo con Invio, non solo dai pulsanti nascosti qui sotto.
    if (solaLettura) {
      return;
    }
    const form = document.getElementById(
      "DipendenteForm",
    ) as HTMLFormElement | null;
    if (form && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const dati: DatiDipendenteDaSalvare = {
      id: employee?.id,
      state: selectedState,
      firstName,
      lastName,
      cf,
      jobDescription,
      workplace,
      hireDate,
      cessationDate,
      email,
      phone,
      birthDate,
      birthPlace,
      nominations: employeeNominations,
      specificRisks: employeeRisks,
      workplaces: workplaces,
      equipments: equipments,
      description: description,
    };

    const esito = await salvaDipendente(dati, isNewEmployee);

    if (esito.esito === "rifiutato") {
      avvisoUnico(AVVISO_SALVATAGGIO, "error", esito.messaggio);
      return;
    }

    avvisoUnico(AVVISO_SALVATAGGIO, "success", t("employee-save-riuscito"));
    if (esito.avviso) {
      avvisoUnico(AVVISO_RILIEVO, "warning", esito.avviso);
    }
    // Lo stato salvato puo' non essere quello scelto: chi ha una data di cessazione gia'
    // trascorsa risulta cessato. La regola vale, quello che mancava era dirlo.
    if (esito.avvisoStato) {
      avvisoUnico(AVVISO_STATO, "info", esito.avvisoStato);
    }
    if (analizza) {
      handleSubmitSuccess(esito.dipendente);
    }
    closeModal();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await salva(true);
  };

  const handleRemoveNomination = async (nominationId: number) => {
    // console.log("handleRemoveNomination called", nominationId);

    // For new employee, just update the local state
    setEmployeeNominations((prev) => prev.filter((n) => n.id !== nominationId));
  };

  // Funzione per aggiungere un nuovo rischio specifico
  const handleAddSpecificRisk = async (newRisk: string) => {
    if (newRisk.trim() === "") return;

    // For new employee, just update the local state
    setEmployeeRisks((prev) => [
      ...prev,
      { id: Date.now(), specificRisk: newRisk },
    ]);
  };

  const handleKeyDownRischi = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddSpecificRisk(e.currentTarget.value);
      (e.target as HTMLInputElement).value = "";
      (e.target as HTMLInputElement).focus();
    }
  };

  const handleRemoveSpecificRisk = async (riskId: number) => {
    // For new employee, just update the local state
    setEmployeeRisks((prev) => prev.filter((r) => r.id !== riskId));
  };

  const handleAddWorkplaces = async (newWorkplace: string) => {
    if (newWorkplace.trim() === "") return;

    // For new employee, just update the local state
    setWorkplaces((prev) => [
      ...prev,
      { id: Date.now(), workplace: newWorkplace },
    ]);
  };

  const handleKeyDownWorkplaces = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddWorkplaces(e.currentTarget.value);
      (e.target as HTMLInputElement).value = "";
      (e.target as HTMLInputElement).focus();
    }
  };

  const handleRemoveWorkplace = async (workplaceId: number) => {
    // For new employee, just update the local state
    setWorkplaces((prev) => prev.filter((w) => w.id !== workplaceId));
  };

  const handleAddEquipments = async (newEquipment: string) => {
    if (newEquipment.trim() === "") return;

    // For new employee, just update the local state
    setEquipments((prev) => [
      ...prev,
      { id: Date.now(), equipment: newEquipment },
    ]);
  };

  const handleKeyDownEquipments = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEquipments(e.currentTarget.value);
      (e.target as HTMLInputElement).value = "";
      (e.target as HTMLInputElement).focus();
    }
  };

  const handleRemoveEquipment = async (equipmentId: number) => {
    // For new employee, just update the local state
    setEquipments((prev) => prev.filter((e) => e.id !== equipmentId));
  };

  const renderNominationValue = (value: string | string[]) => {
    if (Array.isArray(value)) {
      const selectedOptions = nominationsOptions.filter((option) =>
        value.includes(option.name),
      );
      return selectedOptions.length > 0
        ? selectedOptions.map((option) => option.name).join(", ")
        : "Seleziona una nomina...";
    } else {
      const selectedOption = nominationsOptions.find(
        (option) => option.name === value,
      );
      return selectedOption ? selectedOption.name : "Seleziona una nomina...";
    }
  };

  return (
    <>
      <Ariakit.Dialog
        open={isVisible}
        onClose={() => closeModal()}
        className="dialog"
        style={{
          overflowX: "hidden",
        }}
      >
        <Ariakit.DialogHeading className="heading" render={<h2 />}>
          {employee?.id
            ? "Modifica dettagli dipendente"
            : "Inserisci dettagli dipendente"}
          <Ariakit.DialogDismiss
            aria-label="Chiudi"
            style={{
              position: "absolute",
              backgroundColor: "transparent",
              border: "none",
              top: "calc(50% - 12px)",
              right: ".5em",
              cursor: "pointer",
            }}
          >
            <XCircle size={24} color={Color.GREEN} />
          </Ariakit.DialogDismiss>
        </Ariakit.DialogHeading>
        <div
          style={{
            marginTop: "0",
            padding: "0 2rem",
            overflowX: "auto",
          }}
        >
          {
            <form onSubmit={handleSubmit} id="DipendenteForm">
              <button
                type="submit"
                style={{ display: "none" }}
                disabled
                aria-hidden="true"
              />
              <input type="hidden" name="id" value={employee?.id} />
              <FormWrapper>
                <FormField>
                  <ModalLabel htmlFor={"first-name"}>Nome*:</ModalLabel>
                  <Input
                    type="text"
                    id="first-name"
                    name="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </FormField>
                <FormField>
                  <ModalLabel htmlFor={"last-name"}>Cognome*:</ModalLabel>
                  <Input
                    id="last-name"
                    name="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </FormField>
                <FormField>
                  <ModalLabel htmlFor={"fiscal-code"}>
                    Codice fiscale*:
                  </ModalLabel>
                  <Input
                    id="fiscal-code"
                    name="cf"
                    value={cf}
                    onChange={(e) => setCf(e.target.value)}
                    $rilievo={cfDaCorreggere}
                    aria-describedby={
                      cfDaCorreggere ? "rilievo-codice-fiscale" : undefined
                    }
                    required
                  />
                  {cfDaCorreggere && (
                    <RilievoCampo id="rilievo-codice-fiscale" role="status">
                      {t("employee-registry-invalid-fiscal-code")}
                    </RilievoCampo>
                  )}
                </FormField>
                <FormField>
                  <ModalLabel htmlFor={"birth-place"}>
                    Luogo di nascita*:
                  </ModalLabel>
                  <Input
                    id="birth-place"
                    name="birthPlace"
                    value={birthPlace}
                    onChange={(e) => setBirthPlace(e.target.value)}
                    required
                  />
                </FormField>
                <FormField>
                  <ModalLabel htmlFor={"birth-date"}>
                    Data di nascita*:
                  </ModalLabel>
                  <Input
                    id="birth-date"
                    name="birthDate"
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    required
                  />
                </FormField>
                <FormField>
                  <ModalLabel htmlFor={"email"}>Email*:</ModalLabel>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </FormField>
                <FormField>
                  <ModalLabel htmlFor={"workplace"}>
                    {t("employee-registry-sede-di-lavoro")}
                  </ModalLabel>
                  <Ariakit.ComboboxProvider
                    value={workplace}
                    setValue={setWorkplace}
                  >
                    <Ariakit.Combobox
                      id="workplace"
                      name="workplace"
                      required
                      // I suggerimenti si scorrono, ma nessuno si prende da se':
                      // quello che resta scritto e' quello che e' stato digitato.
                      autoSelect={false}
                      autoComplete="list"
                      render={<Input />}
                    />
                    {suggerimentiSede.length > 0 && (
                      <SuggerimentiPopover
                        gutter={4}
                        sameWidth
                        aria-label={t("employee-registry-sedi-suggerite")}
                      >
                        {suggerimentiSede.map((sede) => (
                          <SuggerimentoSede
                            key={sede}
                            value={sede}
                            focusOnHover
                          >
                            {sede}
                          </SuggerimentoSede>
                        ))}
                      </SuggerimentiPopover>
                    )}
                  </Ariakit.ComboboxProvider>
                </FormField>
                <FormField>
                  <ModalLabel htmlFor={"phone"}>Telefono:</ModalLabel>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={10}
                  />
                </FormField>
                <FormField>
                  <ModalLabel htmlFor={"state"}>Stato*:</ModalLabel>
                  <SelectProvider store={stateStore}>
                    {/*
                     Ariakit affianca alla tendina un <select> nascosto che porta
                     lo stesso `name`: l'`aria-label` nomina entrambi, come per
                     le nomine.
                    */}
                    <Select
                      style={selectStyle}
                      id="state"
                      aria-label={t("user-stato-dipendente")}
                      name="state"
                      value={selectedState}
                      renderValue={(value) => {
                        return value
                          ? Status.get(+value)?.label
                          : "Seleziona uno stato...";
                      }}
                      required
                    >
                      {Array.from(Status.entries()).map(
                        ([value, { label }]) => (
                          <SelectItem key={`${value}`} value={`${value}`}>
                            {label}
                          </SelectItem>
                        ),
                      )}
                    </Select>
                  </SelectProvider>
                </FormField>
                <FormField>
                  <ModalLabel htmlFor={"job-description"}>
                    Mansione*:
                  </ModalLabel>
                  <Input
                    id="job-description"
                    name="jobDescription"
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    required
                  />
                </FormField>
                <FormField>
                  <ModalLabel htmlFor={"hire-date"}>
                    Data di assunzione*:
                  </ModalLabel>
                  <Input
                    id="hire-date"
                    name="hireDate"
                    type="date"
                    value={hireDate}
                    onChange={(e) => setHireDate(e.target.value)}
                    required
                  />
                </FormField>
                <FormField>
                  <ModalLabel htmlFor={"cessation-date"}>
                    Data di cessazione:
                  </ModalLabel>
                  <Input
                    id="cessation-date"
                    name="cessationDate"
                    type="date"
                    value={cessationDate}
                    onChange={(e) => setCessationDate(e.target.value)}
                  />
                </FormField>
              </FormWrapper>

              <Fragment>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 2fr",
                    alignItems: "center",
                    gap: "10px",
                    marginTop: "20px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: "bolder",
                      lineHeight: "1em",
                      color: Color.LIGHT_BLUE,
                    }}
                  >
                    Nomine
                  </h3>
                  <SelectProvider store={nominationStore}>
                    <Select
                      style={selectNominationsStyle}
                      aria-label="Nomine"
                      name="nomination"
                      renderValue={renderNominationValue}
                    >
                      {nominationsOptions.map((nomination) => (
                        <SelectItem
                          key={nomination.id}
                          value={nomination.name}
                          disabled={employeeNominations.some(
                            (n) => n.nomination.name === nomination.name,
                          )}
                        >
                          {nomination.name}
                        </SelectItem>
                      ))}
                    </Select>
                  </SelectProvider>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    marginTop: "10px",
                  }}
                >
                  {employeeNominations.map((nomination) => (
                    <BadgeDiv key={nomination.id}>
                      <Label
                        color={Color.DARK_BLUE}
                        text={nomination.nomination.name}
                        fontSize={"14px"}
                      />
                      <button
                        aria-label={`Rimuovi la nomina ${nomination.nomination.name}`}
                        type="button"
                        onClick={() => handleRemoveNomination(nomination.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          color: "red",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <CircleX width={16} height={16} />
                      </button>
                    </BadgeDiv>
                  ))}
                </div>
              </Fragment>

              <Fragment>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 2fr",
                    alignItems: "center",
                    gap: "10px",
                    marginTop: "20px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: "bolder",
                      lineHeight: "1em",
                      color: Color.LIGHT_BLUE,
                    }}
                  >
                    Rischi Specifici
                  </h3>
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <Input
                      id="newSpecificRiskId"
                      style={{ width: "100%" }}
                      type="text"
                      placeholder="Nuovo Rischio Specifico"
                      aria-label="Nuovo Rischio Specifico"
                      onKeyDown={handleKeyDownRischi}
                    />
                    <Suggerimento testo={t("employee-modal-aiuto-aggiungi-voce")}>
                      <Button
                        label=""
                        aria-label="Aggiungi rischio specifico"
                        color={Color.GREEN}
                        icon={Icons.BUTTONS.PLUS}
                        onClick={() => {
                          const inputRisk =
                            document.getElementById("newSpecificRiskId");
                          if (inputRisk) {
                            handleAddSpecificRisk(
                              (inputRisk as HTMLInputElement).value,
                            ).then(() => {
                              (inputRisk as HTMLInputElement).value = "";
                              (inputRisk as HTMLInputElement).focus();
                            });
                          }
                        }}
                        textTransform={"capitalize"}
                      ></Button>
                    </Suggerimento>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    marginTop: "10px",
                  }}
                >
                  {employeeRisks.map((risk) => (
                    <BadgeDiv key={risk.id}>
                      <Label
                        color={Color.DARK_BLUE}
                        text={risk.specificRisk}
                        fontSize={"14px"}
                      />
                      <button
                        aria-label={`Rimuovi il rischio specifico ${risk.specificRisk}`}
                        type="button"
                        onClick={() => handleRemoveSpecificRisk(risk.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          color: "red",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <CircleX width={16} height={16} />
                      </button>
                    </BadgeDiv>
                  ))}
                </div>
              </Fragment>

              <Fragment>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 2fr",
                    alignItems: "center",
                    gap: "10px",
                    marginTop: "20px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: "bolder",
                      lineHeight: "1em",
                      color: Color.LIGHT_BLUE,
                    }}
                  >
                    Luoghi di Lavoro
                  </h3>
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <Input
                      id="newWorkplaceId"
                      style={{ width: "100%" }}
                      type="text"
                      placeholder="Nuovo Luogo di Lavoro"
                      aria-label="Nuovo Luogo di Lavoro"
                      onKeyDown={handleKeyDownWorkplaces}
                    />
                    <Suggerimento testo={t("employee-modal-aiuto-aggiungi-voce")}>
                      <Button
                        label=""
                        aria-label="Aggiungi luogo di lavoro"
                        color={Color.GREEN}
                        icon={Icons.BUTTONS.PLUS}
                        onClick={() => {
                          const inputRisk =
                            document.getElementById("newWorkplaceId");
                          if (inputRisk) {
                            handleAddWorkplaces(
                              (inputRisk as HTMLInputElement).value,
                            ).then(() => {
                              (inputRisk as HTMLInputElement).value = "";
                              (inputRisk as HTMLInputElement).focus();
                            });
                          }
                        }}
                        textTransform={"capitalize"}
                      ></Button>
                    </Suggerimento>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    marginTop: "10px",
                  }}
                >
                  {workplaces.map((workplace) => (
                    <BadgeDiv key={workplace.id}>
                      <Label
                        color={Color.DARK_BLUE}
                        text={workplace.workplace}
                        fontSize={"14px"}
                      />
                      <button
                        aria-label={`Rimuovi il luogo di lavoro ${workplace.workplace}`}
                        type="button"
                        onClick={() => handleRemoveWorkplace(workplace.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          color: "red",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <CircleX width={16} height={16} />
                      </button>
                    </BadgeDiv>
                  ))}
                </div>
              </Fragment>

              <Fragment>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 2fr",
                    alignItems: "center",
                    gap: "10px",
                    marginTop: "20px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: "bolder",
                      lineHeight: "1em",
                      color: Color.LIGHT_BLUE,
                    }}
                  >
                    Attrezzature
                  </h3>
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <Input
                      id="newEquipmentId"
                      style={{ width: "100%" }}
                      type="text"
                      placeholder="Nuova Attrezzatura"
                      aria-label="Nuova Attrezzatura"
                      onKeyDown={handleKeyDownEquipments}
                    />
                    <Suggerimento testo={t("employee-modal-aiuto-aggiungi-voce")}>
                      <Button
                        label=""
                        aria-label="Aggiungi attrezzatura"
                        color={Color.GREEN}
                        icon={Icons.BUTTONS.PLUS}
                        onClick={() => {
                          const inputRisk =
                            document.getElementById("newEquipmentId");
                          if (inputRisk) {
                            handleAddEquipments(
                              (inputRisk as HTMLInputElement).value,
                            ).then(() => {
                              (inputRisk as HTMLInputElement).value = "";
                              (inputRisk as HTMLInputElement).focus();
                            });
                          }
                        }}
                        textTransform={"capitalize"}
                      ></Button>
                    </Suggerimento>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    marginTop: "10px",
                  }}
                >
                  {equipments.map((equipment) => (
                    <BadgeDiv key={equipment.id}>
                      <Label
                        color={Color.DARK_BLUE}
                        text={equipment.equipment}
                        fontSize={"14px"}
                      />
                      <button
                        aria-label={`Rimuovi l'attrezzatura ${equipment.equipment}`}
                        type="button"
                        onClick={() => handleRemoveEquipment(equipment.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          color: "red",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <CircleX width={16} height={16} />
                      </button>
                    </BadgeDiv>
                  ))}
                </div>
              </Fragment>
              <FormField>
                <ModalLabel htmlFor={"description"}>
                  Descrizione del lavoro
                </ModalLabel>
                <TextArea
                  id="description"
                  name="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </FormField>
              {/*
                Gli attestati del dipendente, cessato compreso: e' l'unico punto da cui si
                vedono e si scaricano quelli di chi non e' piu' in forza. Solo su un
                dipendente gia' censito: di uno che si sta inserendo non ce ne sono.
              */}
              {!isNewEmployee && employee?.id && (
                <AttestatiDipendente employeeId={employee.id} />
              )}
              <ButtonContainer>
                {!isNewEmployee && employee?.id && puoEliminare && (
                  <EliminaAllineatoASinistra>
                    <Suggerimento testo={t("employee-modal-aiuto-elimina")}>
                      <Button
                        type="button"
                        onClick={() => setConfermaEliminazione(true)}
                        label={t("employee-modal-delete")}
                        textTransform={"capitalize"}
                        color={Color.RED}
                        fontSize={"16px"}
                      ></Button>
                    </Suggerimento>
                  </EliminaAllineatoASinistra>
                )}
                <Button
                  onClick={() => {
                    (
                      document.getElementById(
                        "DipendenteForm",
                      ) as HTMLFormElement
                    )?.reset();
                    closeModal();
                  }}
                  label={t("close")}
                  textTransform={"capitalize"}
                  color={Color.WHITE}
                  textColor={Color.LIGHT_BLUE}
                  fontSize={"16px"}
                ></Button>
                {!solaLettura && (<>
                <Suggerimento testo={t("employee-modal-aiuto-salva")}>
                  <Button
                    type="button"
                    onClick={() => {
                      void salva(false);
                    }}
                    label={t("employee-modal-save")}
                    textTransform={"capitalize"}
                    color={Color.GREEN}
                    fontSize={"16px"}
                    style={{ padding: "0.5rem 1rem", height: "2.4rem" }}
                  ></Button>
                </Suggerimento>
                <Suggerimento testo={t("employee-modal-aiuto-salva-analizza")}>
                  <SubmitButton
                    label={t("employee-modal-save-and-analyze")}
                    form="DipendenteForm"
                    color={Color.BLUE}
                    name="SubmitButton"
                    style={{ padding: "0.5rem 1rem" }}
                  />
                </Suggerimento>
                </>)}
              </ButtonContainer>
            </form>
          }
          {/*
           La conferma vive dentro il modale che la apre, e non accanto: un Ariakit.Dialog
           modale rende inerte tutto cio' che sta fuori dal proprio albero, quindi una dialog
           dichiarata li' fuori si vedrebbe ma non prenderebbe un solo click — ne' i bottoni,
           ne' la X. Annidata, Ariakit la riconosce come figlia e le passa l'interazione.
          */}
          <DialogConferma
            aperta={confermaEliminazione}
            titolo={t("employee-delete-conferma-titolo")}
            messaggio={t("employee-delete-conferma-messaggio", {
              nome: `${employee?.lastName ?? ""} ${
                employee?.firstName ?? ""
              }`.trim(),
            })}
            etichettaConferma={t("employee-delete-conferma-si")}
            etichettaAnnulla={t("employee-delete-conferma-no")}
            coloreConferma={Color.RED}
            coloreAnnulla={Color.BLUE}
            onConferma={eliminaConfermato}
            onAnnulla={() => setConfermaEliminazione(false)}
          />
        </div>
      </Ariakit.Dialog>
    </>
  );
};

export default ModalEmployeeEdit;
